import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaBookOpen, FaClock, FaCheckCircle, FaArrowRight } from "react-icons/fa";

const ToeflReadingInstructions = ({ mock = 1 }) => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const points = [
        "Complete the Words: type the missing letters to complete words in a short paragraph.",
        "Read in Daily Life: answer questions about everyday texts such as notices, emails and menus.",
        "Read an Academic Passage: read a short academic text and answer five questions.",
        "The section is adaptive: your Module 1 answers decide whether Module 2 is harder or easier. Only the harder Module 2 allows the highest bands.",
        "You can move between tasks within a module, but you cannot return to Module 1 once you continue.",
        "Each module is timed separately and submits automatically when time runs out. There is no penalty for guessing.",
    ];

    return (
        <>
            <style>{`
                .toefl-reading-container {
                    min-height: 100vh;
                    background: #fafafa;
                    padding: 2rem;
                    padding-top: 96px;
                    display: flex;
                    align-items: flex-start;
                    justify-content: center;
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                }

                .content-wrapper {
                    width: 100%;
                    max-width: 720px;
                    background: #ffffff;
                    border-radius: 20px;
                    padding: 3rem 3.25rem;
                    border: 1px solid #e8e8e8;
                    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.07);
                }

                .icon-badge {
                    width: 68px;
                    height: 68px;
                    border-radius: 16px;
                    background: rgba(10, 137, 78, 0.08);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 1.5rem auto;
                }

                .icon-badge svg {
                    font-size: 1.9rem;
                    color: #0a894e;
                }

                .main-title {
                    font-size: 2.1rem;
                    font-weight: 800;
                    margin: 0 0 0.6rem 0;
                    color: #0a894e;
                    text-align: center;
                    letter-spacing: -0.01em;
                }

                .subtitle {
                    font-size: 1.05rem;
                    color: #666;
                    text-align: center;
                    margin-bottom: 2.25rem;
                }

                .info-cards {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 1rem;
                    margin-bottom: 2rem;
                }

                .info-card {
                    background: #f7f8f7;
                    border-radius: 14px;
                    padding: 1.25rem 1.4rem;
                    border: 1px solid #e5e5e5;
                }

                .info-card-header {
                    display: flex;
                    align-items: center;
                    gap: 0.6rem;
                    margin-bottom: 0.6rem;
                    color: #0a894e;
                }

                .info-card-header h3 {
                    margin: 0;
                    color: #111;
                    font-size: 0.95rem;
                    font-weight: 700;
                }

                .info-card p {
                    color: #444;
                    margin: 0;
                    font-size: 0.9rem;
                    line-height: 1.5;
                }

                .info-card p strong {
                    color: #111;
                }

                .section-title {
                    font-size: 0.85rem;
                    font-weight: 700;
                    color: #0a894e;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    margin: 0 0 1rem 0;
                }

                .conditions-box {
                    background: #f7f8f7;
                    border-radius: 14px;
                    padding: 0.25rem 1.5rem;
                    border: 1px solid #e5e5e5;
                    margin-bottom: 2.25rem;
                }

                .conditions-list {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                }

                .condition-item {
                    color: #333;
                    padding: 1rem 0;
                    font-size: 0.98rem;
                    border-bottom: 1px solid #e8e8e8;
                    display: flex;
                    align-items: flex-start;
                    line-height: 1.5;
                }

                .condition-item:last-child {
                    border-bottom: none;
                }

                .condition-item::before {
                    content: "";
                    min-width: 7px;
                    height: 7px;
                    border-radius: 50%;
                    background: #0a894e;
                    margin-right: 0.9rem;
                    margin-top: 0.5rem;
                    flex-shrink: 0;
                }

                .start-button-wrap {
                    text-align: center;
                }

                .start-button {
                    background: #0a894e;
                    color: #fff;
                    border: none;
                    padding: 1rem 2.75rem;
                    font-size: 1.05rem;
                    font-weight: 700;
                    border-radius: 999px;
                    cursor: pointer;
                    display: inline-flex;
                    align-items: center;
                    gap: 0.6rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 14px rgba(10, 137, 78, 0.25);
                }

                .start-button:hover {
                    transform: scale(1.04);
                    box-shadow: 0 8px 20px rgba(10, 137, 78, 0.3);
                }

                /* Responsive Design */
                @media (max-width: 760px) {
                    .content-wrapper {
                        max-width: 100%;
                        padding: 2.25rem 1.75rem;
                    }

                    .info-cards {
                        grid-template-columns: 1fr;
                    }
                }

                @media (max-width: 560px) {
                    .toefl-reading-container {
                        padding: 1rem;
                        padding-top: 76px;
                    }

                    .content-wrapper {
                        padding: 1.75rem 1.5rem;
                        border-radius: 16px;
                    }

                    .main-title {
                        font-size: 1.6rem;
                    }

                    .subtitle {
                        font-size: 0.95rem;
                    }

                    .condition-item {
                        font-size: 0.9rem;
                    }

                    .start-button {
                        width: 100%;
                        justify-content: center;
                    }
                }
            `}</style>

            <div className="toefl-reading-container">
                <div className="content-wrapper">
                    <div className="icon-badge">
                        <FaBookOpen />
                    </div>

                    <h1 className="main-title">Reading Section</h1>
                    <p className="subtitle">
                        TOEFL iBT (2026 format): complete words, read
                        everyday texts and answer questions on an academic passage.
                    </p>

                    <div className="info-cards">
                        <div className="info-card">
                            <div className="info-card-header">
                                <FaClock size={16} />
                                <h3>Time Limit</h3>
                            </div>
                            <p>
                                About <strong>30 minutes</strong>:{" "}
                                <strong>16 min</strong> for Module 1 and{" "}
                                <strong>14 min</strong> for Module 2.
                            </p>
                        </div>

                        <div className="info-card">
                            <div className="info-card-header">
                                <FaCheckCircle size={16} />
                                <h3>Format</h3>
                            </div>
                            <p>
                                <strong>2 adaptive modules</strong> ·{" "}
                                <strong>3 task types</strong> · scored on
                                the <strong>1–6 band</strong> scale.
                            </p>
                        </div>
                    </div>

                    <h2 className="section-title">
                        About the Reading Section
                    </h2>

                    <div className="conditions-box">
                        <ul className="conditions-list">
                            {points.map((point, index) => (
                                <li className="condition-item" key={index}>
                                    {point}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="start-button-wrap">
                        <button
                            className="start-button"
                            onClick={() => navigate(mock === 1 ? "/quiz/toefl/reading" : `/quiz/toefl-mock${mock}/reading`)}
                        >
                            Start Reading Test
                            <FaArrowRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ToeflReadingInstructions;